
import React, { useState, useEffect, useCallback } from 'react'
import { makeStyles, Link, Collapse, debounce } from '@material-ui/core'
import PropTypes from 'prop-types'
import ScrollIntoView from 'react-scroll-into-view'
import dayjs from 'dayjs'
import { useClient } from '../../session/clientSession'
import { replaceVariables, taskValueVariables, templateValueVariables } from '../../../utils/variables'
import RenderImage from './renderImage'
import RenderIframe from './renderIframe'
import RenderLink from './renderLink'
import RenderTextBlock from './renderTextBlock'
import RenderTextInput from './renderTextInput.js'
import RenderDropdown from './renderDropdown.js'
import RenderRadioButton from './renderRadioButton.js'
import RenderRange from './renderRange.js'
import RenderConfirmation from './renderConfirmation.js'
import RenderTable from './renderTable.js'
import RenderChecklist from './renderChecklist.js'
import RenderUpload from './renderUpload.js'
import { checkConditionalRules } from '../../../utils/rules'
import { useClientTask } from 'components/session/clientTaskSession'
import RenderLineBreak from './renderLineBreak'
import { useSession } from 'components/session/session'
import RenderTemplatePDF from './renderPDFTemplate'

const useStyles = makeStyles(() => ({
  link: {
    color: 'var(--card-text)',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  formElm: {
    margin: '10px 0px 20px 0px'
  }
}))

export function saveItemValue({ newValue, task, elmIndex, valIndex, clientTaskSession }) {
  const newValueString = JSON.stringify(newValue)
  if (!task.Components[elmIndex].Value) task.Components[elmIndex].Value = []
  if (newValueString === JSON.stringify(task.Components[elmIndex].Value[valIndex])) return
  task.Components[elmIndex].Value[valIndex] = JSON.parse(newValueString)
  delete task.Components[elmIndex].__isLocal
  clientTaskSession.setTask(task)
}

export default function CreateForm({ selectedVersion, allowModify, preview, passedTask, setSelectedVersion, taskSession }) {
  const classes = useStyles()
  const clientSession = useClient()
  const clientTaskSession = useClientTask()
  const [showItem, setShowItem] = useState(null)
  const [content, setContent] = useState(null)
  const [missing, setMissing] = useState(null)
  const [taskVariables, setTaskVariables] = useState({})
  const [templateVariables, setTemplateVariables] = useState(null)
  const [localVersion, setLocalVersion] = useState(null)
  const [lastUpdate, setLastUpdate] = useState(dayjs(1))

  const task = passedTask || clientTaskSession.task || {}

  if (!task.Components) task.Components = []
  if (!Array.isArray(task.Components)) task.Components = Object.values(task.Components)

  const debounceRender = useCallback(debounce(() => setContent([]), 200), [])

  useEffect(() => {
    setShowItem(task.Components.reduce((newShow, elm, elmIndex) => checkConditionalRules(elm, elmIndex, selectedVersion, task.Components, newShow), []))
  }, [selectedVersion])

  // Create initial show/hide 
  if (!showItem) {
    setShowItem(task.Components.reduce((newShow, elm, elmIndex) => checkConditionalRules(elm, elmIndex, selectedVersion, task.Components, newShow), []))
    const taskCopy = JSON.parse(JSON.stringify(task, getCircularReplacer()))
    setTaskVariables(taskValueVariables(taskCopy))
    templateValueVariables(taskCopy, clientSession).then(vars => setTemplateVariables(vars))
  }

  const valueChange = (elmIndex, valIndex, newValue) => {
    saveItemValue({ newValue, task, elmIndex, valIndex, clientTaskSession: taskSession || clientTaskSession })
    // Check if conditionals changed and re-render
    const newShow = task.Components.reduce((newShow, elm, elmIndex) => checkConditionalRules(elm, elmIndex, selectedVersion, task.Components, newShow), [])
    setShowItem(newShow)
    const newTaskVariables = taskValueVariables(JSON.parse(JSON.stringify(task, getCircularReplacer())))
    if (JSON.stringify(taskVariables) != JSON.stringify(newTaskVariables)) {
      setTaskVariables(newTaskVariables)
      if (debounceRender) debounceRender()
    }
  }

  // Check if the data has been updated by someone else
  const remoteLastUpdate = dayjs(task.LastUpdated && task.LastUpdated.toDate ? task.LastUpdated.toDate() : 0)

  // when "save and add another" or deletion of a row, we set the selected version to -2 in order to force a re-render
  // This will set the selected version back to -1 once everything is cleared
  useEffect(() => {
    if (selectedVersion < -1) setSelectedVersion(-1)
  })


  // Wait for template varaibles before loading
  if (!templateVariables) return <></>

  // Main render loop. Only run if things have changed from the previous render.
  if (!content || content.length != task.Components?.length || lastUpdate.isBefore(remoteLastUpdate.subtract('5', 'second')) || localVersion != selectedVersion) {

    let reloadReason = 'no content'
    if (content && !content.length != task.Components?.length) reloadReason = 'length mismatch'
    if (lastUpdate.isBefore(remoteLastUpdate.subtract('5', 'second'))) reloadReason = 'remote update'
    if (localVersion != selectedVersion) reloadReason = 'version mismatch'
    console.log(reloadReason)

    // when "save and add another" or deletion of a row, we set the selected version to -2 in order to force a re-render
    // This will "clear" the content
    if (selectedVersion < -1 && content?.length > 0) {
      setContent([])
      setMissing([])
    }

    else {
      const localContent = [], localMissing = []
      task.Components.forEach((elm, elmIndex) => {
        if (!showItem) return
        if (!elm.Value || elm.Value.length === 0) elm.Value = {}
        if (!clientSession.client) clientSession.client = {}
        if (!clientSession.client.Metadata) clientSession.client.Metadata = {}

        // Client Variables
        elm = replaceVariables(elm, clientSession.client.Metadata.Variables || {})

        // Task Variables
        const taskVariablesForVersion = {}
        Object.keys(taskVariables).forEach(key => {
          if (Array.isArray(taskVariables?.[key]) || typeof taskVariables?.[key] === 'object') {
            let value = (taskVariables?.[key]?.[selectedVersion] || [])
            if (!Array.isArray(value)) value = [value]
            taskVariablesForVersion[key] = value.join(', ') || '____'
          }
          else taskVariablesForVersion[key] = taskVariables?.[key] || '____'
        })
        elm = replaceVariables(elm, taskVariablesForVersion)

        // Template Variables
        const templateVariablesForVersion = {}
        Object.keys(templateVariables).forEach(key => {
          if (Array.isArray(templateVariables?.[key]) || typeof templateVariables?.[key] === 'object') templateVariablesForVersion[key] = (templateVariables?.[key]?.[selectedVersion] || []).join(', ') || '____'
          else templateVariablesForVersion[key] = templateVariables?.[key] || '____'
        })
        elm = replaceVariables(elm, templateVariablesForVersion)

        const c = <FormItem elm={elm} elmIndex={elmIndex} valIndex={selectedVersion} valueChange={valueChange} templateData={clientSession.client.Metadata} allowModify={allowModify} preview={preview} />
        localContent.push(c)
        const m = <MissingFormItem elm={elm} elmIndex={elmIndex} valIndex={selectedVersion} hasBeenUpdated={!!task.LastClientUpdated} classes={classes} />
        localMissing.push(m)
      })
      setLastUpdate(remoteLastUpdate)
      setContent(localContent)
      setMissing(localMissing)
      setLocalVersion(selectedVersion)
    }
  }

  return <>
    {missing && missing.map((m, elmIndex) => <Collapse key={elmIndex} in={showItem ? showItem[elmIndex] : false}>{m}</Collapse>)}
    {content && content.map((c, elmIndex) => <Collapse key={elmIndex} in={showItem ? showItem[elmIndex] : false}><div className={`${classes.formElm}`} id={`_elm_holder_${elmIndex}`}>{c}</div></Collapse>)}
  </>
}

CreateForm.propTypes = {
  selectedVersion: PropTypes.number,
  allowModify: PropTypes.bool.isRequired,
  preview: PropTypes.bool,
  passedTask: PropTypes.object,
  setSelectedVersion: PropTypes.func.isRequired,
  taskSession: PropTypes.object
}

export function FormItem({ elm, elmIndex, valIndex, valueChange, allowModify, preview }) {
  const session = useSession()
  const clientSession = useClient()
  let out = <></>
  if (elm.Type === 'image') {
    out = <RenderImage elmIndex={elmIndex} elm={elm} />
  } else if (elm.Type === 'iFrame') {
    out = <RenderIframe elmIndex={elmIndex} elm={elm} />
  } else if (elm.Type === 'link') {
    out = <RenderLink elmIndex={elmIndex} elm={elm} />
  } else if (elm.Type === 'textBlock') {
    out = <RenderTextBlock elmIndex={elmIndex} elm={elm} />
  } else if (elm.Type === 'lineBreak') {
    out = <RenderLineBreak elmIndex={elmIndex} elm={elm} />
  } else if (elm.Type === 'fileUpload') {
    if (elm.ClientReadOnly && (session.external || clientSession.forceClientView)) {
      out = elm?.Value?.[valIndex]?.map(Prompt => <RenderImage key={Prompt} elmIndex={elmIndex} elm={{ Prompt }} />) ?? <></>
    }
    else out = <RenderUpload elmIndex={elmIndex} elm={elm} valIndex={valIndex} valueChange={valueChange} allowModify={allowModify} />
  } else if (elm.Type === 'dropdown') {
    out = <RenderDropdown elmIndex={elmIndex} elm={elm} valIndex={valIndex} valueChange={valueChange} allowModify={allowModify} />
  } else if (elm.Type === 'radioButtons' || elm.Type === 'checkboxes') {
    out = <RenderRadioButton elmIndex={elmIndex} elm={elm} valIndex={valIndex} valueChange={valueChange} allowModify={allowModify} />
  } else if (elm.Type === 'tableInput') {
    out = <RenderTable elmIndex={elmIndex} elm={elm} valIndex={valIndex} valueChange={valueChange} allowModify={allowModify} preview={preview} />
  } else if (elm.Type === 'range') {
    out = <RenderRange elmIndex={elmIndex} elm={elm} valIndex={valIndex} valueChange={valueChange} allowModify={allowModify} />
  } else if (elm.Type === 'textInput') {
    out = <RenderTextInput elmIndex={elmIndex} elm={elm} valIndex={valIndex} valueChange={valueChange} allowModify={allowModify} />
  } else if (elm.Type === 'confirmation') {
    out = <RenderConfirmation elmIndex={elmIndex} elm={elm} valIndex={valIndex} valueChange={valueChange} allowModify={allowModify} />
  } else if (elm.Type === 'checklist') {
    out = <RenderChecklist elmIndex={elmIndex} elm={elm} valIndex={valIndex} valueChange={valueChange} allowModify={allowModify} />
  } else if (elm.Type === 'templatePDF') {
    out = <RenderTemplatePDF elmIndex={elmIndex} elm={elm} valIndex={valIndex} valueChange={valueChange} />
  }
  return out
}

FormItem.propTypes = {
  elm: PropTypes.object.isRequired,
  elmIndex: PropTypes.number.isRequired,
  valIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  valueChange: PropTypes.func.isRequired,
  allowModify: PropTypes.bool.isRequired,
  preview: PropTypes.bool
}

function MissingFormItem({ elm, elmIndex, valIndex, classes, hasBeenUpdated }) {
  if (!elm.Value || elm.Value.length === 0) elm.Value = {}
  if (elm.Type === 'image' || elm.Type === 'iFrame' || elm.Type === 'link' || elm.Type === 'textBlock' || elm.Type === 'lineBreak' || elm.Type === 'templatePDF') return <></>
  let missingElement = <></>
  // Show unfinished tasks on top
  if (hasBeenUpdated && valIndex != -1) {
    if (!elm.AllowBlanks // If blanks are allowed, skip
      &&
      ((elm.Type === 'tableInput' && Object.values(elm.Value[valIndex] || {}).some((e, i) => Object.values(e || {}).some((e, j) => {
        return !elm.Table?.[i]?.[j]?.Prompt && !(e.Value || elm.Table?.[i]?.[j]?.AllowBlanks)
      }))) // For table elements, loop through the values, and check if at least one element is blank but NOT a prompt
        ||
        (elm.Type === 'range' && (!elm.Value[valIndex] || !elm.Value[valIndex][0] || !elm.Value[valIndex][1])) // Check both elements for range elements
        ||
        (elm.Type === 'checklist' && (!elm.Value[valIndex] || !Object.values(elm.Value[valIndex] || {}).every(e => e))) // Check that all checklist items are complete
        ||
        (!elm.Value[valIndex] || !elm.Value[valIndex][0])) // For Non-Table, check the valIndex exists, and then check the first element to make sure it is not blank
    ) {
      missingElement = <ScrollIntoView selector={`#_elm_holder_${elmIndex}`} onClick={() => {
        const d = document.querySelector(`#_elm_holder_${elmIndex}`)
        if (d) d.classList.add('highlight')
        setTimeout(() => { window.scrollBy(0, -64) }, 500)
        setTimeout(() => { if (d) d.classList.remove('highlight') }, 2000)
      }}>
        <Link className={classes.link}><span style={{ color: 'red', fontSize: '0.9em' }}>Incomplete: </span>{elm.Prompt || `Question #${elmIndex + 1}`}</Link>
      </ScrollIntoView >
    }
  }
  return missingElement
}

MissingFormItem.propTypes = {
  elm: PropTypes.object.isRequired,
  elmIndex: PropTypes.number.isRequired,
  valIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  classes: PropTypes.object.isRequired,
  hasBeenUpdated: PropTypes.bool.isRequired,
}

const getCircularReplacer = () => {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cyclic_object_value
  const seen = new WeakSet()
  return (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return
      seen.add(value)
    }
    return value
  }
}